import React from "react";
import { SpotRequestFilters } from "@/common/model/SpotRequest";
import { MagnifyingGlassIcon } from "@heroicons/react/24/outline";

interface RequestFiltersProps {
  filters: SpotRequestFilters;
  onFiltersChange: (filters: SpotRequestFilters) => void;
  counts: {
    all: number;
    merge: number;
    add: number;
    rename: number;
    edit: number;
  };
}

const RequestFilters: React.FC<RequestFiltersProps> = ({
  filters,
  onFiltersChange,
  counts,
}) => {
  return (
    <div className="space-y-2">
      {/* Type filter buttons */}
      <div className="flex flex-wrap gap-1">
        <button
          onClick={() => onFiltersChange({ ...filters, requestType: "all" })}
          className={`
            px-2 py-1 text-xs rounded font-medium transition-colors
            ${filters.requestType === "all"
              ? "bg-gray-600 text-white"
              : "bg-gray-700 text-gray-400 hover:bg-gray-600"
            }
          `}
        >
          All ({counts.all})
        </button>
        <button
          onClick={() => onFiltersChange({ ...filters, requestType: "merge" })}
          className={`
            px-2 py-1 text-xs rounded font-medium transition-colors
            ${filters.requestType === "merge"
              ? "bg-purple-600 text-white"
              : "bg-gray-700 text-gray-400 hover:bg-gray-600"
            }
          `}
        >
          <span className="flex items-center gap-1">
            <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4" />
            </svg>
            Merge ({counts.merge})
          </span>
        </button>
        <button
          onClick={() => onFiltersChange({ ...filters, requestType: "add" })}
          className={`
            px-2 py-1 text-xs rounded font-medium transition-colors
            ${filters.requestType === "add"
              ? "bg-green-600 text-white"
              : "bg-gray-700 text-gray-400 hover:bg-gray-600"
            }
          `}
        >
          <span className="flex items-center gap-1">
            <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
            Add ({counts.add})
          </span>
        </button>
        <button
          onClick={() => onFiltersChange({ ...filters, requestType: "rename" })}
          className={`
            px-2 py-1 text-xs rounded font-medium transition-colors
            ${filters.requestType === "rename"
              ? "bg-blue-600 text-white"
              : "bg-gray-700 text-gray-400 hover:bg-gray-600"
            }
          `}
        >
          <span className="flex items-center gap-1">
            <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
            </svg>
            Rename ({counts.rename})
          </span>
        </button>
        <button
          onClick={() => onFiltersChange({ ...filters, requestType: "edit" })}
          className={`
            px-2 py-1 text-xs rounded font-medium transition-colors
            ${filters.requestType === "edit"
              ? "bg-amber-600 text-white"
              : "bg-gray-700 text-gray-400 hover:bg-gray-600"
            }
          `}
        >
          <span className="flex items-center gap-1">
            <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
            </svg>
            Edit ({counts.edit})
          </span>
        </button>
      </div>

      {/* Sort dropdown row */}
      <div className="flex items-center gap-2">
        <select
          value={filters.sortBy}
          onChange={(e) => onFiltersChange({ ...filters, sortBy: e.target.value as SpotRequestFilters["sortBy"] })}
          className="flex-1 px-2 py-1.5 text-xs bg-gray-700 border border-gray-600 rounded text-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500"
        >
          <option value="newest">Newest</option>
          <option value="oldest">Oldest</option>
          <option value="distance">Distance</option>
          <option value="sessions">Sessions</option>
        </select>

        {/* Search input */}
        <div className="relative flex-[2]">
          <MagnifyingGlassIcon className="absolute left-2 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400" />
          <input
            type="text"
            value={filters.searchQuery}
            onChange={(e) => onFiltersChange({ ...filters, searchQuery: e.target.value })}
            placeholder="Search..."
            className="w-full pl-7 pr-7 py-1.5 text-xs bg-gray-700 border border-gray-600 rounded text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
          {filters.searchQuery && (
            <button
              onClick={() => onFiltersChange({ ...filters, searchQuery: "" })}
              className="absolute right-2 top-1/2 -translate-y-1/2"
            >
              <svg className="w-3.5 h-3.5 text-gray-400 hover:text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default RequestFilters;
